import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNavC from './StepNavC';
import ConfirmModal from './ConfirmModal';
import {
  recommendationCases,
  recCaseStatusMeta,
  defaultRecCaseId,
  paramStatusMeta,
  worker,
} from '../../mock/modulec';
import '../moduleb/moduleb-common.css';
import './ModuleCRecommend.css';

function ModuleCRecommend() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(defaultRecCaseId);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState(false);

  const selected = recommendationCases.find((c) => c.id === selectedId) || recommendationCases[0];
  const isApproved = approvedIds.has(selected.id);

  const handleConfirm = () => {
    setApprovedIds((prev) => new Set(prev).add(selected.id));
    setModalOpen(false);
    setToast(true);
    setTimeout(() => setToast(false), 4000);
  };

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNavC current={1} />

        {/* 성공 토스트 */}
        {toast && (
          <div className="mc-toast">
            <span className="material-symbols-outlined">check_circle</span>
            {t('pages.modulec.recommend.approvedBanner')}
            <button className="mc-toast-action" onClick={() => navigate('/module-c/safeguard')}>
              {t('pages.modulec.recommend.goSafeguard')} <span className="material-symbols-outlined">arrow_forward</span>
            </button>
            <button className="mc-toast-close" onClick={() => setToast(false)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag mc-tag">STEP 1</span>{t('pages.modulec.recommend.title')}
            </div>
            <div className="mb-page-subtitle">
              {t('pages.modulec.recommend.subtitle')}
            </div>
          </div>
          <div className="mb-page-meta">{t('pages.modulec.recommend.meta')} <strong>{selected.shotId}</strong> · <strong>{selected.productId}</strong></div>
        </div>

        <div className="mb-body">
          <div className="mc-recommend-layout">
            {/* 좌측: 권고 조건 리스트 */}
            <div className="mb-card mc-rec-list-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">lightbulb</span>{t('pages.modulec.recommend.listTitle')}
                <span className="mc-rec-count">{t('pages.modulec.common.countSuffix', { n: recommendationCases.length })}</span>
              </div>
              <div className="mc-rec-list">
                {recommendationCases.map((c) => {
                  const status = approvedIds.has(c.id) ? 'approved' : c.status;
                  const meta = recCaseStatusMeta[status];
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`mc-rec-item ${c.id === selectedId ? 'active' : ''}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <div className="mc-rec-item-top">
                        <span className="mc-rec-item-product">{c.productId}</span>
                        <span className="mb-badge" style={{ color: meta.color, background: meta.bg }}>{t(meta.labelKey)}</span>
                      </div>
                      <div className="mc-rec-item-shot">{c.id} · {c.shotId}</div>
                      <div className="mc-rec-item-defects">
                        {c.defectKeys.map((d) => (
                          <span key={d} className="mc-rec-defect-chip">{d}</span>
                        ))}
                        <span className="mc-rec-item-reduction">{c.predictedReduction}%</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 우측: 선택된 권고 조건 내용 */}
            <div className="mc-recommend-content">
              {/* 권고 조건 테이블 */}
              <div className="mb-card">
                <div className="mb-card-title">
                  <span className="material-symbols-outlined">tune</span>{t('pages.modulec.recommend.paramTitle')}
                  <span className="mc-rec-content-id">{selected.id}</span>
                </div>
                <div className="mb-table-wrap">
                  <table className="mb-table mc-param-table">
                    <thead>
                      <tr>
                        <th>{t('pages.modulec.recommend.cols.param')}</th>
                        <th style={{ textAlign: 'right' }}>{t('pages.modulec.recommend.cols.current')}</th>
                        <th style={{ textAlign: 'right' }}>{t('pages.modulec.recommend.cols.recommended')}</th>
                        <th style={{ textAlign: 'right' }}>{t('pages.modulec.recommend.cols.delta')}</th>
                        <th style={{ textAlign: 'center' }}>{t('pages.modulec.recommend.cols.range')}</th>
                        <th style={{ textAlign: 'center' }}>{t('pages.modulec.recommend.cols.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.params.map((p) => {
                        const meta = paramStatusMeta[p.status];
                        return (
                          <tr key={p.key} className={p.tempFactor ? 'mc-row-temp' : ''}>
                            <td>
                              {t(p.labelKey)} <span className="mc-unit">({p.unit})</span>
                              {p.tempFactor && <span className="mc-temp-tag">{t('pages.modulec.common.tempTag')}</span>}
                            </td>
                            <td className="mb-mono" style={{ textAlign: 'right' }}>{p.current}</td>
                            <td className="mb-mono" style={{ textAlign: 'right', color: '#22c55e', fontWeight: 700 }}>{p.recommended}</td>
                            <td className="mb-mono" style={{ textAlign: 'right', color: p.delta > 0 ? '#f97316' : '#3B82F6' }}>
                              {p.delta > 0 ? '+' : ''}{p.delta}
                            </td>
                            <td className="mb-mono" style={{ textAlign: 'center', color: '#9CA3AF' }}>{p.min} ~ {p.max}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className="mb-badge" style={{ color: meta.color, background: meta.bg }}>{t(meta.labelKey)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 권고 요약 + 승인 */}
              <div className="mb-grid mb-mt mc-summary-grid">
                <div className="mb-card mc-summary-card">
                  <div className="mc-summary-main">
                    <div className="mc-summary-reduction">
                      <span className="mc-summary-reduction-label">{t('pages.modulec.recommend.expectedReduction')}</span>
                      <span className="mc-summary-reduction-value">{selected.predictedReduction}%</span>
                    </div>
                    <div className="mc-summary-divider" />
                    <div className="mc-summary-conf">
                      <span className="mc-summary-conf-label">{t('pages.modulec.recommend.confidence')}</span>
                      <span className="mc-summary-conf-value">{selected.confidence}<small>%</small></span>
                    </div>
                  </div>
                  <div className="mc-summary-note">
                    <span className="material-symbols-outlined">lightbulb</span>
                    {t(selected.noteKey)}
                  </div>
                </div>

                <div className="mb-card mc-action-card">
                  {isApproved ? (
                    <>
                      <div className="mc-approved-state">
                        <span className="material-symbols-outlined">verified</span>
                        {t('pages.modulec.recommend.approvedShort')}
                      </div>
                      <button className="mc-primary-btn" onClick={() => navigate('/module-c/safeguard')}>
                        <span className="material-symbols-outlined">arrow_forward</span>
                        {t('pages.modulec.recommend.goSafeguard')}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="mc-action-text">
                        {t('pages.modulec.recommend.approveHint')}
                      </div>
                      <button className="mc-primary-btn" onClick={() => setModalOpen(true)}>
                        <span className="material-symbols-outlined">task_alt</span>
                        {t('pages.modulec.recommend.approveBtn')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 권고 승인 확인 모달 */}
      <ConfirmModal
        isOpen={modalOpen}
        type="warning"
        title={t('pages.modulec.recommend.modalTitle')}
        message={t('pages.modulec.recommend.modalMessage', { product: selected.productId, shot: selected.shotId })}
        changes={selected.params.map((p) => ({ labelKey: p.labelKey, from: p.current, to: p.recommended, unit: p.unit }))}
        warning={t('pages.modulec.recommend.modalWarning')}
        workerName={`${t(worker.nameKey)} (${t(worker.shiftKey)})`}
        confirmText={t('pages.modulec.modal.confirmApply')}
        cancelText={t('pages.modulec.modal.cancel')}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleConfirm}
      />
    </MainLayout>
  );
}

export default ModuleCRecommend;
